import { parse, eval as evaluate } from 'expression-eval';
import PuzzleLocation from './PuzzleLocation';
import PuzzleModelEvent from './PuzzleModelEvent';
import PuzzleStatus from './PuzzleStatus';
import PuzzleMove from './PuzzleMove';
import EventDispatcher from '../../../../../shared/lib/html5-software-library/src/ranj/util/EventDispatcher';
import { getURLParameter } from '../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil';

export default class PuzzleModel extends EventDispatcher {

    /**
     * 
     * @param {Object} puzzleConfig - object (parsed json)
     */
    constructor(puzzleConfig) {
        super();
        this._config = puzzleConfig;

        this._actors = this._config.actors;

        this._parseContraintSets();

        this._attempts = [];//array of Attempts

        const debugTime = parseInt(getURLParameter("time"));
        this._availableTime = (!isNaN(debugTime) ? debugTime : this._config.time) * 1000;
        this._elapsedTime = 0;

        this.reset();

        const failed = this._applyConstraints(ConstraintSetId.FAILURE, ConstraintSetId.BOAT).length > 0;
        if (failed) {
            throw new Error(`Initial state for puzzle '${this.id}' violates constraints.`);
        }
    }

    get id() {
        return this._config.id;
    }

    get config() {
        return this._config;
    }

    get status() {
        return this._status;
    }

    get failedConstraints() {
        return this._failedConstraints;
    }

    get moveCount() {
        return this.attempt.moveCount;
    }
    get totalMoveCount() {
        return this._attempts.reduce((sum, att) => sum + att.moveCount, 0);
    }

    get failedAttemptCount() {
        return this._attempts.filter(att => att.success === false).length;
    }

    get attempt() {
        return this._attempts[this._attempts.length - 1];
    }


    addElapsedTime(dt) {
        this._elapsedTime += dt;
        this.dispatchEvent(PuzzleModelEvent.ELAPSED_TIME, this._elapsedTime);
        if (this._status == PuzzleStatus.STARTED && this.remainingTime <= 0 && !this._timedOut) {
            this._timedOut = true;
            this._setStatus(PuzzleStatus.TIMEOUT);
        }
    }

    get minimumMoves() {
        return this._config.minMoves;
    }

    get availableTime() {
        return this._availableTime;
    }

    get elapsedTime() {
        return this._elapsedTime;
    }

    get remainingTime() {
        return this._availableTime - this._elapsedTime;
    }


    start() {
        this._setStatus(PuzzleStatus.STARTED);
    }

    reset() {
        //Reset state: 
        //  which actor is where, where is the boat, moves etc
        this._actorsByLocation = {};
        this._boatLocation = null;
        this._timedOut = false;
        this._attempts.push(new Attempt());
        this._applyInitialState();
        this._setStatus(PuzzleStatus.NONE);
    }

    /**
     * @param {PuzzleStatus} value 
     */
    _setStatus(value) {
        if (this._status != value) {
            this._status = value;
            this.dispatchEvent(PuzzleModelEvent.STATUS, this._status);
        }
    }

    crossRiver() {
        const snapshot = this.getState();
        switch (this._boatLocation) {
            case PuzzleLocation.BANK_FROM:
                this._boatLocation = PuzzleLocation.BANK_TO;
                break;
            case PuzzleLocation.BANK_TO:
                this._boatLocation = PuzzleLocation.BANK_FROM;
                break;
        }
        //first check if move is valid
        const failedConditions = this._applyConstraints(ConstraintSetId.CROSSING);
        if (failedConditions.length > 0) {
            this._applySnapshot(snapshot);
            this.dispatchEvent(PuzzleModelEvent.MOVE_INVALID, { failedConditions: failedConditions, actor: null, location: PuzzleLocation.BOAT });
        } else {
            //check 'real' constraints for result state
            this._wrapupMove(PuzzleMove.CROSS);
        }
    }

    switchActorLocation(actorId) {
        const loc = this._getLocationForActor(actorId);
        switch (loc) {
            case PuzzleLocation.BOAT:
                return this.setActorLocation(actorId, this._boatLocation);
            default:
                return this.setActorLocation(actorId, PuzzleLocation.BOAT);
        }
    }

    setActorLocation(actorId, location) {
        const snapshot = this.getState();
        this.removeActorFromLocation(actorId);
        this._actorsByLocation[location].add(actorId);
        //first check if move is valid
        //apply constraints, and rollback if constraints fail
        const failedConditions = this._applyConstraints(ConstraintSetId.BOAT);
        if (failedConditions.length) {
            this._applySnapshot(snapshot);
            this.dispatchEvent(PuzzleModelEvent.MOVE_INVALID, { failedConditions: failedConditions, actor: actorId, location: location });
        } else {
            const moveType = location == PuzzleLocation.BOAT ? PuzzleMove.EMBARK : PuzzleMove.DISEMBARK;
            this._wrapupMove(moveType, actorId);
        }
    }

    _wrapupMove(moveType, actorId = null) {
        //All moveTypes count as moves, not just crossings
        this.attempt.addMove(new PuzzleMove(moveType));

        this._failedConstraints = this._applyConstraints(ConstraintSetId.FAILURE);
        const failed = this._failedConstraints.length > 0;
        this.dispatchEvent(PuzzleModelEvent.MOVE, { type: moveType, count: this.moveCount, actor: actorId });
        if (failed) {
            this.attempt.success = false;
            this._setStatus(PuzzleStatus.FAILED);
        } else {
            if (this._checkGoalState()) {
                this.attempt.success = true;
                this._setStatus(PuzzleStatus.COMPLETED);
            };
        }
    }

    removeActorFromLocation(actorId) {
        return PuzzleLocation.ALL.forEach(location => {
            this._actorsByLocation[location].delete(actorId);
        })

    }

    _getLocationForActor(actorId) {
        return PuzzleLocation.ALL.find(location => {
            return this._actorsByLocation[location].has(actorId);
        })
    }

    _applyConstraints(...constraintSetIds) {
        return constraintSetIds.reduce((all, constraintSetId) => {
            const failed = this._constraints[constraintSetId].filter(constraint => {
                if (PuzzleLocation.ALL.indexOf(constraint.location) > -1) {
                    const context = this._createEvaluationContext(constraint.location);
                    const ok = evaluate(constraint.ast, context);
                    return !ok;
                } else {
                    return false;
                }
            });

            failed.forEach(({ id, location, rule }) => {
                console.log(`Failed: ${id} ${location} ${rule}`);
            });

            return all.concat(failed);
        }, []);
    }

    _createEvaluationContext(location) {
        const isBoat = location == PuzzleLocation.BOAT;
        const counts = {};
        let weight = 0;
        this._actors.forEach(actor => { counts[actor.id] = 0; });
        const countActorsForLocation = (loc, addWeight = false) => {
            this._actorsByLocation[loc].forEach(actorId => {
                counts[actorId]++;
                if (addWeight) {
                    weight += this.getActorWeight(actorId);
                }
            });
        }
        countActorsForLocation(location, isBoat);
        if (!isBoat) {
            if (this._boatLocation == location) {
                countActorsForLocation(PuzzleLocation.BOAT, true);
            }
        }
        return new EvaluationContext(counts, weight);
    }

    _applyInitialState() {
        this._applyState(this._config.initialState);
    }

    _applyState(state) {
        PuzzleLocation.ALL.forEach(location => {
            this._actorsByLocation[location] = new Set();
            const actors = state.actors[location];
            if (actors) {
                actors.forEach(actorId => {
                    this._actorsByLocation[location].add(actorId);
                });
            }
        });
        this._boatLocation = state.boat;
    }

    _checkGoalState() {
        const state = this._config.goalState;
        return PuzzleLocation.ALL.every(location => {
            const actors = state.actors[location];
            if (!actors) {
                return true;
            }
            return actors.every(actorId => {
                return this._actorsByLocation[location].has(actorId);
            });
        });
    }

    getState() {
        //return clone, so we can use as snapshot
        const locations = {};
        for (let location in this._actorsByLocation) {
            locations[location] = new Set(this._actorsByLocation[location]);
        }
        const state = { locations: locations, boat: this._boatLocation };
        return state;
    }

    _applySnapshot(state) {
        this._actorsByLocation = state.locations;
        this._boatLocation = state.boat;
    }

    getActorWeight(actorId) {
        const actor = this.getActorParams(actorId);
        return actor != null ? actor.weight : 0;
    }

    getActorParams(actorId) {
        return this._actors.find(actor => actor.id == actorId);
    }

    _parseContraintSets() {
        this._constraints = {};
        const constraintSetDefs = this._config.constraints;
        ConstraintSetId.ALL.forEach(id => {
            this._constraints[id] = parseConstraints(constraintSetDefs[id]);
        });
    }

    complete() {
        this.attempt.success = true;
        this._applyState(this._config.goalState);
        this._setStatus(PuzzleStatus.COMPLETED);
    }

    fail() {
        this.attempt.success = false;
        this._failedConstraints = this._constraints[ConstraintSetId.FAILURE];
        this._setStatus(PuzzleStatus.FAILED);
    }

    /*****************************
     *      SAVEGAME RELATED
     *****************************/

    toObject() {
        //make state serializable (convert Sets into Arrays)
        const state = this.getState();
        for (let loc in state.locations) {
            state.locations[loc] = [...state.locations[loc]];
        }
        return {
            status: this._status,
            state: state,
            elapsedTime: this._elapsedTime,
            attempts: this._attempts.map(attempt => attempt.toObject())
        }
    }

    /**
     * 
     * @param {Object} object - see keys in PuzzleModel.toObject()
     */
    setFromObject(object) {
        this._attempts = object.attempts.map(att => Attempt.fromObject(att));
        this._elapsedTime = object.elapsedTime;
        this.addElapsedTime(0);//to trigger event
        const state = object.state;
        if (state) {
            for (let loc in state.locations) {
                state.locations[loc] = new Set(state.locations[loc]);
            }
            this._applySnapshot(state);
        }
    }
};


const parseConstraints = function(constraintDefs) {
    const constraints = [];
    constraintDefs.forEach(({ id, rule, locations, actors }) => {
        const parsedRule = parse(rule);
        locations.forEach(location => {
            if (PuzzleLocation.ALL.indexOf(location) == -1) {
                throw new Error(`Unknown location '${location}' in constraint '${id}'`)
            }
            constraints.push({ id: id, location: location, ast: parsedRule, rule: rule, actors: actors });
        });
    });
    return constraints;
}


class EvaluationContext {
    constructor(counts, weight) {
        this.weight = weight;
        this.count = (actorId) => {
            return counts[actorId];
        }
        //add shortcut props
        for (const actorId in counts) {
            this[actorId] = counts[actorId];
        }
    }
}

class Attempt {
    constructor() {
        /**@type {Number} */
        this.moves = 0;
        this.success = null;
    }

    /**
     * @param {PuzzleMove} move 
     */
    addMove(move) {
        this.moves++;
    }

    get moveCount() {
        return this.moves;
    }

    toObject() {
        return {
            success: this.success,
            moves: this.moves
        }
    }

    static fromObject(obj) {
        const res = new Attempt();
        res.success = obj.success;
        res.moves = obj.moves;
        return res;
    }
}

const ConstraintSetId = {};
ConstraintSetId.CROSSING = "crossing";
ConstraintSetId.BOAT = "boat";
ConstraintSetId.FAILURE = "failure";
ConstraintSetId.ALL = Object.values(ConstraintSetId);